import type { ElementType } from 'react';

import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';

import UniversalPreviewRenderer from '@/features/DataHub/modules/Export/components/Preview/UniversalPreviewRenderer';
import { useExportPreview } from '@/features/DataHub/modules/Export/context/exportContexts';
import type { ExportOption } from '@/features/DataHub/modules/Export/types';

import type { Theme } from '@mui/material/styles';

export const ExportGroup = ({
  groupName,
  GroupIcon,
  options
}: {
  groupName: string;
  GroupIcon: ElementType;
  options: ExportOption[];
}) => {
  return (
    <Box>
      <Box
        sx={theme => ({
          display: 'flex',
          alignItems: 'center',
          fontSize: theme.typography.pxToRem(14),
          fontWeight: 600
        })}
      >
        <GroupIcon
          fontSize='small'
          sx={(theme: Theme) => ({ mr: 1, width: theme.typography.pxToRem(16) })}
        />
        {groupName}
      </Box>
      <Box sx={{ mt: 2, gap: 2, display: 'flex', flexDirection: 'column' }}>
        {options.map(option => (
          <ExportItem
            key={option.id}
            option={option}
          />
        ))}
      </Box>
    </Box>
  );
};

const ExportItem = ({ option }: { option: ExportOption }) => {
  const { setPreviewOptionId, previewOptionId } = useExportPreview();
  const { id, label, icon, disabled } = option;
  return (
    <Accordion
      disabled={disabled}
      expanded={previewOptionId === id}
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{
        border: '1px solid',
        borderColor: previewOptionId === id ? 'white' : 'transparent',
        backgroundColor: 'secondary.main',

        px: 1
      }}
      onChange={() => {
        if (previewOptionId === id) {
          setPreviewOptionId(null);
        } else {
          setPreviewOptionId(id);
        }
      }}
    >
      <AccordionSummary sx={{ p: 0, height: '38px' }}>
        <Box
          sx={theme => ({
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 1,
            fontSize: theme.typography.pxToRem(16),
            fontWeight: 600
          })}
        >
          <Box
            component='img'
            src={icon.url}
            sx={theme => ({
              height: theme.typography.pxToRem(icon.size)
            })}
          />
          {label}
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          p: 0,
          pb: 1
        }}
      >
        {previewOptionId === id && (
          <UniversalPreviewRenderer
            exportOption={option}
            previewConfig={option.preview}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ExportGroup;
